import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import CustomDialog from '@ui-components/Dialog/CustomDialog';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomCheckBox from '@ui-components/CheckBox/CustomCheckBox';
import { type ResumeConsultResponse } from '../../API/services/resumeConsultServices';
import {
  useResumeConsults,
  useResumeSelectedConsults,
} from '../../API/hooks/resumeConsultHook';
import CrossIcon from '@assets/images/Cross.svg';
import ConfirmationPopup from '../components/ConfirmationPopup';
import CustomTooltip from '@qler-view/shared_lib/src/shared/lib/ui-components/ToolTip/CustomToolTip';
import { getUserFromStorage } from '../components/storageUtility';
import './facility.css';
import { CONSULT_WAITING_SCREEN_ROUTE_PATH } from '../../routers/RoutPath';
import { useNavigate } from 'react-router-dom';
import {
  stopAlert,
  initAudio,
  warmUpAudio,
} from '../../shared/lib/utils/audioManager';
import AlertSound from '@assets/ringtone.mp3';

interface FetchResumableConsultsProps {
  openResumedConsultUsers: boolean;
  onConsultClose: () => void;
}

const FetchResumableConsults: React.FC<FetchResumableConsultsProps> = ({
  openResumedConsultUsers,
  onConsultClose,
}) => {
  const user = getUserFromStorage();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string>('');
  const { ResumeConsults, isLoading: isLoadingResumedUsers } =
    useResumeConsults(user?.userId ?? 0, sessionId);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] =
    useState<ResumeConsultResponse | null>(null);
  const [searchTermResumedConsultUsers, setSearchTermResumedConsultUsers] =
    useState<string>('');
  const [openConfirmationPopup, setOpenConfirmationPopup] =
    useState<boolean>(false);

  const handleConsultUserSearch = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTermResumedConsultUsers(event.target.value);
  };

  const selectResumeConsultUser = (user: ResumeConsultResponse) => {
    setSelectedUser(user);
  };

  const deselectResumeConsultUser = () => {
    setSelectedUser(null);
  };

  const handleResumeSuccess = () => {
    if (selectedUser) {
      setSearchTermResumedConsultUsers('');
      setOpenConfirmationPopup(true);
      navigate(
        `${CONSULT_WAITING_SCREEN_ROUTE_PATH}?consultId=${selectedUser.consultId}`,
        { replace: true }
      );
    }
  };

  const { mutate: resumeConsult } =
    useResumeSelectedConsults(handleResumeSuccess);

  useEffect(() => {
    if (openResumedConsultUsers) {
      setSearchTermResumedConsultUsers('');
      setSessionId('');
      setErrorMessage('');
    }
  }, [openResumedConsultUsers]);

  return (
    <Box>
      <Box>
        <ConfirmationPopup
          openPopup={openConfirmationPopup}
          onClosePopup={() => {
            setOpenConfirmationPopup(false);
          }}
          title={'Alert!'}
          message={
            'Doctor has already joined the call. Please click on Join button to join the call.'
          }
          onConfirm={() => {
            stopAlert();
            setOpenConfirmationPopup(false);
          }}
        />
      </Box>
      <CustomDialog
        id="Resume-Consult-user"
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        open={openResumedConsultUsers}
        onClose={() => {
          onConsultClose();
        }}
      >
        <CustomDialogContent className="resume-consult">
          <Box>
            <Box className="flex-justify-space-between">
              <CustomTypography
                id="resume-consult-title"
                variant="h5"
                color="textPrimary"
              >
                Resume Consult
              </CustomTypography>
              <CustomIconButton
                id="dialog-close"
                icon={<CrossIcon />}
                onClick={() => onConsultClose()}
              />
            </Box>

            <CustomTypography
              id="sub-text"
              variant="body3"
              color="textDisabled"
            >
              Please enter session ID to join a session
            </CustomTypography>
          </Box>

          <Box className="flex-justify-space-between mt-24">
            <CustomTextField
              id="txt-search-resume-consult-user"
              placeholder="Type to search by Session ID"
              variant="outlined"
              size="small"
              type="text"
              value={searchTermResumedConsultUsers}
              onChange={handleConsultUserSearch}
            />
            {sessionId && (
              <CustomButton
                id="btn-search-consult-for-resume"
                variant="contained"
                className="ml-12 height-40"
                onClick={() => {
                  const trimmed = searchTermResumedConsultUsers.trim();
                  if (trimmed) {
                    setErrorMessage(null);
                  } else {
                    setErrorMessage('Please enter a session ID');
                  }
                  deselectResumeConsultUser();
                  setSessionId(trimmed);
                }}
              >
                Search
              </CustomButton>
            )}
          </Box>
          <Box>
            {errorMessage && (
              <CustomTypography
                id="error-message"
                color="error"
                variant="body2"
              >
                {errorMessage}
              </CustomTypography>
            )}
          </Box>
          {sessionId && (
            <Box className="pt-20">
              {isLoadingResumedUsers && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="90px"
                >
                  <CustomTypography
                    id="loading-text"
                    variant="h5"
                    className="text-align-center pt-32"
                    color="textDisabled"
                  >
                    Loading...
                  </CustomTypography>
                </Box>
              )}
              {!isLoadingResumedUsers && (
                <Box>
                  <CustomTable
                    stickyHeader={true}
                    id="table-resume-consult-list"
                    tableContainerClassName="table-container-for-popup"
                  >
                    <CustomTableHead id="table-head-resume-consult-list">
                      <CustomTableRow id="resume-consult-user-header">
                        <CustomTableCell
                          id="table-cell-select"
                          className="checkbox-cell"
                        ></CustomTableCell>
                        <CustomTableCell id="table-cell-session-id">
                          SESSION ID
                        </CustomTableCell>
                        <CustomTableCell id="table-cell-assigned-provider">
                          ASSND. PROVIDER
                        </CustomTableCell>
                        <CustomTableCell id="table-cell-unit-department">
                          UNIT/DEP
                        </CustomTableCell>
                        <CustomTableCell id="table-cell-status">
                          STATUS
                        </CustomTableCell>
                        <CustomTableCell id="table-cell-reason">
                          REASON
                        </CustomTableCell>
                      </CustomTableRow>
                    </CustomTableHead>
                    {ResumeConsults && ResumeConsults?.length > 0 ? (
                      <CustomTableBody id="table-body-partner">
                        {ResumeConsults?.map((user) => (
                          <CustomTableRow
                            key={user.sessionId}
                            id={`table-row-resume-consult-user-${user.sessionId}`}
                          >
                            <CustomTableCell
                              id={`table-cell-resume-consult-id-${user.sessionId}`}
                              className="checkbox-cell"
                            >
                              <CustomCheckBox
                                id={`checkbox-resume-consult-id-${user.sessionId}`}
                                checked={
                                  selectedUser?.sessionId === user.sessionId
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    selectResumeConsultUser(user);
                                  } else {
                                    deselectResumeConsultUser();
                                  }
                                  setErrorMessage(null);
                                }}
                              />
                            </CustomTableCell>

                            <CustomTableCell
                              id={`table-cell-resume-consult-session-id-${user.sessionId}`}
                            >
                              {user.sessionId || 'N/A'}
                            </CustomTableCell>
                            <CustomTableCell
                              id={`table-cell-resume-consult-assigned-provider-${user.assignedProvider}`}
                            >
                              {user.assignedProvider || 'N/A'}
                            </CustomTableCell>
                            <CustomTableCell
                              id={`table-cell-resume-consult-unit-department-${user.unitDepartment}`}
                            >
                              {user.unitDepartment || 'N/A'}
                            </CustomTableCell>
                            <CustomTableCell
                              id={`table-cell-resume-consult-status-${user.status}`}
                            >
                              {user.status || 'N/A'}
                            </CustomTableCell>
                            <CustomTableCell
                              id={`table-cell-resume-consult-reason-for-consult-${user.reasonForConsult}`}
                              className="ellipsis-cell"
                            >
                              <CustomTooltip
                                id="Reason-tooltip-resume-consult"
                                title={user.reasonForConsult}
                                placement="bottom-end"
                              >
                                <span>{user.reasonForConsult || 'N/A'}</span>
                              </CustomTooltip>
                            </CustomTableCell>
                          </CustomTableRow>
                        ))}
                      </CustomTableBody>
                    ) : (
                      <CustomTableBody id="table-body-resume-consult">
                        <CustomTableRow id="table-row-resume-consult">
                          <CustomTableCell
                            id="table-cell-resume-consult-id"
                            colspan={9}
                          >
                            <Box className="flex flex-justify-center align-items-center">
                              <CustomTypography
                                id="no-entra-users-found"
                                variant="body3"
                                color="textDisabled"
                              >
                                No Consults Found
                              </CustomTypography>
                            </Box>
                          </CustomTableCell>
                        </CustomTableRow>
                      </CustomTableBody>
                    )}
                  </CustomTable>
                </Box>
              )}
            </Box>
          )}
          <Box className="flex justify-end pt-24">
            <Grid2 container>
              <Grid2 className="mr-12">
                <CustomButton
                  id="btnYes"
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    onConsultClose();
                    setErrorMessage(null);
                    setSelectedUser(null);
                    setSearchTermResumedConsultUsers('');
                    setSessionId('');
                  }}
                >
                  Cancel
                </CustomButton>
              </Grid2>
              <Grid2>
                <CustomButton
                  id="btnConfirm-complete"
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    setErrorMessage(null);
                    if (!sessionId) {
                      const trimmed = searchTermResumedConsultUsers.trim();
                      if (!trimmed) {
                        setErrorMessage('Please enter a session ID');
                        return;
                      }
                      deselectResumeConsultUser();
                      setSessionId(trimmed);
                      return;
                    }

                    if (!selectedUser) {
                      setErrorMessage('Please select a consult');
                      return;
                    }

                    // Unlock audio on this real user gesture (needed for iOS/iPadOS)
                    initAudio(AlertSound);
                    warmUpAudio();

                    resumeConsult({
                      userId: user?.userId ?? 0,
                      consultId: selectedUser.consultId,
                    });
                  }}
                >
                  {sessionId ? 'Resume' : 'Search'}
                </CustomButton>
              </Grid2>
            </Grid2>
          </Box>
        </CustomDialogContent>
      </CustomDialog>
    </Box>
  );
};

export default FetchResumableConsults;
